import React from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import AssetActions from '../actions/AssetActions';
import AssetQueryBuilder from './AssetQueryBuilder';
import type { QueryBuilderHandle } from './AssetQueryBuilder';
import { getApiErrorMessage } from '@shared/api/apiClient';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';

const { useState, useRef, useCallback, useReducer, useImperativeHandle } = React;

interface QueryRuleNode {
    field?: string;
    operator?: string;
    value?: unknown;
    type?: string;
    condition?: string;
    rules?: QueryRuleNode[];
}

interface AssetQueryFormProps {
    assetClasses: IAssetClassDetail[];
    initialRules: QueryRuleNode | undefined;
    onOK: (apiRules: QueryRuleNode | null, cleanedRules: QueryRuleNode | null) => Promise<void>;
}

interface AssetQueryFormHandle {
    open: () => void;
    close: () => void;
}

const AssetQueryForm = React.forwardRef((props: AssetQueryFormProps, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [queryError, setQueryError] = useState<string>('');
    const queryBuilderRef = useRef<QueryBuilderHandle>(null);

    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const handleClose = () => {
        setQueryError('');
        setIsOpen(false);
    };

    useImperativeHandle(ref, () => ({
        open: () => {
            setQueryError('');
            setIsOpen(true);
        },
        close: () => {
            setQueryError('');
            setIsOpen(false);
        },
    }), []);

    const handleQueryRulesChange = useCallback((_rules: QueryRuleNode | null) => {
        forceUpdate();
    }, []);

    const saveQuery = async () => {
        setQueryError('');
        if (!queryBuilderRef.current?.hasValidRules()) {
            AssetActions.clearAssetQuery();
            setIsOpen(false);
            return;
        }

        const cleanedRules = queryBuilderRef.current?.getCleanedRules();
        const apiRules = queryBuilderRef.current?.getApiRules();

        try {
            await props.onOK(apiRules, cleanedRules);
            setIsOpen(false);
        } catch (error) {
            setQueryError(getApiErrorMessage(error).message || InovxLoader.t('facility:message.queryExecutionFailed'));
        }
    };

    const { run: handleOK, isInFlight: isSaving } = useAsyncActionLock(saveQuery);

    const renderFooter = () => (
        <div>
            <div className="d-flex j-cc">
                <div>
                    <a
                        id="facilityApp-save-btn"
                        aria-disabled={isSaving}
                        onClick={isSaving ? undefined : () => void handleOK()}
                        className={`btn btn-primary${isSaving ? ' disabled' : ''}`}
                    >
                        {InovxLoader.t("facility:form.ok")}
                    </a>
                </div>
                <div className="ml-1">
                    <a
                        id="facilityApp-cancel-btn"
                        aria-disabled={isSaving}
                        onClick={isSaving ? undefined : handleClose}
                        className={`btn btn-default${isSaving ? ' disabled' : ''}`}
                    >
                        {InovxLoader.t("facility:preferences.cancelButton")}
                    </a>
                </div>
            </div>
        </div>
    );

    return (
        <DialogComponent
            width="60%"
            style={{ minWidth: '750px' }}
            header={InovxLoader.t('facility:form.queryBuilder')}
            visible={isOpen}
            showCloseIcon={true}
            close={handleClose}
            isModal={true}
            target="#app"
            zIndex={110000}
            footerTemplate={renderFooter}
        >
            <div>
                {isOpen && (
                    <AssetQueryBuilder
                        ref={queryBuilderRef}
                        assetClasses={props.assetClasses}
                        initialRules={props.initialRules}
                        onRulesChange={handleQueryRulesChange}
                    />
                )}
                {queryError && <div className="query-builder-form-error">{queryError}</div>}
            </div>
        </DialogComponent>
    );
});

export default AssetQueryForm;
